/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Plus, FileText, Calendar, Clock,
  AlertTriangle, Trash2, Download, Zap, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { PdfLanguageDialog } from '../components/callsheet/PdfLanguageDialog';
import { downloadCallSheetPdf } from '../utils/callsheetPdf';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  draft:     { label: 'Draft',     bg: 'var(--cb-surface-a)', color: 'var(--cb-text-sec)' },
  published: { label: 'Published', bg: 'rgba(34,197,94,0.15)', color: '#22C55E' },
};

export default function CallSheetListPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [sheets, setSheets]         = useState([]);
  const [scheduleDays, setScheduleDays] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const [selectedPdfSheet, setSelectedPdfSheet] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [form, setForm]             = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    shooting_day: 1,
    total_shooting_days: 1,
    unit: 'A Unit',
    crew_call: '07:00',
    day_night: 'Day',
    schedule_day_id: '',
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchSheets();
    fetchScheduleDays();
    // eslint-disable-next-line
  }, [projectId]);

  async function fetchSheets() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets`, { headers });
      if (r.ok) setSheets(await r.json());
    } finally {
      setLoading(false);
    }
  }

  async function fetchScheduleDays() {
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets-schedule-days`, { headers });
      if (r.ok) setScheduleDays(await r.json());
    } catch (error) { console.error('Failed to load call sheet schedule days', error); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...form,
          shooting_day: parseInt(form.shooting_day),
          total_shooting_days: parseInt(form.total_shooting_days),
          schedule_day_id: form.schedule_day_id || null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const sheet = await r.json();
      toast.success('Call sheet created');
      setShowCreate(false);
      navigate(`/projects/${projectId}/callsheet/${sheet.id}`);
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    setDeleting(id);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets/${id}`, {
        method: 'DELETE', headers
      });
      if (!r.ok) throw new Error();
      setSheets(s => s.filter(x => x.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  }

  const totalScenes = (s) => s.scenes?.length || 0;
  const totalCast   = (s) => s.cast_call_times?.length || 0;

  async function handleDownloadPdf(synopsisLang) {
    if (!selectedPdfSheet) return;
    setDownloadingPdf(true);
    try {
      await downloadCallSheetPdf({
        apiBase: API,
        projectId,
        sheetId: selectedPdfSheet.id,
        token,
        synopsisLang,
        filename: `callsheet_day${selectedPdfSheet.shooting_day || 1}_${selectedPdfSheet.date || 'draft'}.pdf`,
      });
      toast.success('PDF downloaded!');
      setSelectedPdfSheet(null);
    } catch (error) {
      toast.error(error.message || 'PDF download failed');
    } finally {
      setDownloadingPdf(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--cb-bg)' }} data-testid="callsheet-list-page">
      {/* Header */}
      <div className="px-6 py-5" style={{ borderBottom: '1px solid var(--cb-border)', background: 'var(--cb-surface)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--cb-text)', fontFamily: "'Cabinet Grotesk', 'DM Sans', 'Noto Sans Malayalam', 'Manjari', sans-serif" }}>
              Daily Call Sheets
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--cb-text-sec)' }}>
              Mission-control documents for each shooting day
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="gap-2"
            style={{ background: 'var(--cb-accent)', color: '#000', fontWeight: 600 }}
            data-testid="create-callsheet-btn"
          >
            <Plus className="h-4 w-4" />
            New Call Sheet
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20" style={{ color: 'var(--cb-text-dim)' }}>Loading…</div>
        ) : sheets.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
                 style={{ background: 'var(--cb-accent-dim)' }}>
              <FileText className="h-8 w-8" style={{ color: 'var(--cb-accent)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--cb-text)' }}>No call sheets yet</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--cb-text-sec)' }}>
              Create your first daily call sheet for this production
            </p>
            <Button onClick={() => setShowCreate(true)} style={{ background: 'var(--cb-accent)', color: '#000' }}>
              <Plus className="h-4 w-4 mr-2" /> Create Call Sheet
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sheets.map(sheet => {
              const badge = STATUS_BADGE[sheet.status] || STATUS_BADGE.draft;
              return (
                <div
                  key={sheet.id}
                  className="rounded-xl overflow-hidden cursor-pointer group transition-all duration-200 hover:translate-y-[-2px]"
                  style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
                  onClick={() => navigate(`/projects/${projectId}/callsheet/${sheet.id}`)}
                  data-testid={`callsheet-card-${sheet.id}`}
                >
                  {/* Card top accent */}
                  <div className="h-1" style={{ background: 'var(--cb-accent)' }} />

                  <div className="p-5">
                    {/* Status + version */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                            style={{ background: badge.bg, color: badge.color }}>
                        {badge.label}
                      </span>
                      <span className="text-xs font-mono" style={{ color: 'var(--cb-text-dim)' }}>
                        {sheet.version_label || 'v1'}
                      </span>
                    </div>

                    {/* Title + day */}
                    <h3 className="text-base font-bold mb-1 leading-tight" style={{ color: 'var(--cb-text)' }}>
                      {sheet.title}
                    </h3>
                    <div className="text-xl font-black font-mono mb-3" style={{ color: 'var(--cb-accent)' }}>
                      Day {sheet.shooting_day} <span className="text-sm font-normal" style={{ color: 'var(--cb-text-dim)' }}>of {sheet.total_shooting_days}</span>
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--cb-text-sec)' }}>
                        <Calendar className="h-3.5 w-3.5" />
                        {sheet.date}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--cb-text-sec)' }}>
                        <Clock className="h-3.5 w-3.5" />
                        {sheet.crew_call || '—'}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--cb-text-sec)' }}>
                        <FileText className="h-3.5 w-3.5" />
                        {totalScenes(sheet)} scenes
                      </div>
                    </div>

                    {/* Stats pills */}
                    <div className="flex gap-2 flex-wrap mb-4">
                      {[
                        { label: `${totalScenes(sheet)} Scenes`, accent: false },
                        { label: `${totalCast(sheet)} Cast`, accent: false },
                        { label: sheet.unit, accent: false },
                        { label: sheet.day_night, accent: true },
                      ].map(({ label, accent }) => (
                        <span key={label} className="text-[10px] px-2 py-0.5 rounded font-medium"
                              style={{
                                background: accent ? 'var(--cb-accent-dim)' : 'var(--cb-bg)',
                                color: accent ? 'var(--cb-accent)' : 'var(--cb-text-dim)',
                                border: '1px solid var(--cb-border)'
                              }}>
                          {label}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-3" style={{ borderTop: '1px solid var(--cb-border)' }}
                         onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="gap-1.5 flex-1 text-xs"
                              style={{ color: 'var(--cb-text-sec)' }}
                              onClick={() => navigate(`/projects/${projectId}/callsheet/${sheet.id}`)}>
                        <Eye className="h-3.5 w-3.5" /> Open
                      </Button>
                      <Button size="sm" variant="ghost" className="gap-1.5 text-xs"
                              style={{ color: 'var(--cb-text-dim)' }}
                              onClick={() => setSelectedPdfSheet(sheet)}
                              data-testid={`download-cs-${sheet.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="gap-1.5 text-xs"
                              style={{ color: 'var(--cb-text-dim)' }}
                              onClick={() => handleDelete(sheet.id)}
                              disabled={deleting === sheet.id}
                              data-testid={`delete-cs-${sheet.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: 'var(--cb-surface)', borderColor: 'var(--cb-border)' }}
                       className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--cb-text)' }}>Create Daily Call Sheet</DialogTitle>
            <DialogDescription style={{ color: 'var(--cb-text-sec)' }}>
              Set up a new call sheet for a shooting day
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>
                Call Sheet Title *
              </label>
              <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                     placeholder="e.g. Day 3 – INT Locations"
                     style={{ background: 'var(--cb-bg)', borderColor: 'var(--cb-border)', color: 'var(--cb-text)' }}
                     data-testid="cs-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Date</label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}
                       style={{ background: 'var(--cb-bg)', borderColor: 'var(--cb-border)', color: 'var(--cb-text)' }}
                       data-testid="cs-date-input" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Crew Call</label>
                <Input type="time" value={form.crew_call} onChange={e => setForm(f => ({...f, crew_call: e.target.value}))}
                       style={{ background: 'var(--cb-bg)', borderColor: 'var(--cb-border)', color: 'var(--cb-text)' }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Shoot Day #</label>
                <Input type="number" min="1" value={form.shooting_day}
                       onChange={e => setForm(f => ({...f, shooting_day: e.target.value}))}
                       style={{ background: 'var(--cb-bg)', borderColor: 'var(--cb-border)', color: 'var(--cb-text)' }} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Total Days</label>
                <Input type="number" min="1" value={form.total_shooting_days}
                       onChange={e => setForm(f => ({...f, total_shooting_days: e.target.value}))}
                       style={{ background: 'var(--cb-bg)', borderColor: 'var(--cb-border)', color: 'var(--cb-text)' }} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Unit</label>
                <select value={form.unit} onChange={e => setForm(f => ({...f, unit: e.target.value}))}
                        className="w-full rounded-md text-sm px-3 py-2 outline-none"
                        style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}>
                  {['A Unit','B Unit','C Unit','Second Unit','Insert Unit'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>

            {/* FIPR Sync option */}
            {scheduleDays.length > 0 && (
              <div>
                <label className="text-xs font-medium mb-1.5 flex items-center gap-1.5" style={{ color: 'var(--cb-text-sec)' }}>
                  <Zap className="h-3.5 w-3.5" style={{ color: 'var(--cb-accent)' }} />
                  Auto-sync from Schedule (optional)
                </label>
                <select value={form.schedule_day_id}
                        onChange={e => setForm(f => ({...f, schedule_day_id: e.target.value}))}
                        className="w-full rounded-md text-sm px-3 py-2 outline-none"
                        style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}
                        data-testid="cs-sync-select">
                  <option value="">— No auto-sync —</option>
                  {scheduleDays.map(d => (
                    <option key={d.id} value={d.id}>
                      Day {d.day_number}: {d.day_name} ({d.scene_ids?.length || 0} scenes)
                    </option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: 'var(--cb-text-dim)' }}>
                  Pulls scenes, cast & locations from your existing schedule
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}
                      style={{ borderColor: 'var(--cb-border)', color: 'var(--cb-text-sec)' }}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}
                      style={{ background: 'var(--cb-accent)', color: '#000', fontWeight: 700 }}
                      data-testid="create-cs-submit">
                {creating ? 'Creating…' : 'Create Call Sheet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PdfLanguageDialog
        open={Boolean(selectedPdfSheet)}
        onClose={() => setSelectedPdfSheet(null)}
        onConfirm={handleDownloadPdf}
        loading={downloadingPdf}
        title="Choose call sheet PDF synopsis language"
      />
    </div>
  );
}
